import { useMemo } from "react";
import { AskMonitoringChat } from "./AskMonitoringChat";
import {
  computeMonitorRoutes,
  filterWorkflowSummaries,
  groupRoutesByWorkflow,
  matchingRoutes,
  type MonitorRouteRow,
  ROUTE_AUTH_MODES,
  type RouteAuthMode,
  sortWorkflowRouteSummaries,
  type WorkflowRouteSort,
} from "../../../shared/monitoringRouteAuth";
import { queries } from "../../../shared/queries";
import { config } from "../../environment";
import { useQuery } from "@rocicorp/zero/react";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { routeAuthLabel } from "../../lib/routeAuthDisplay";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { useMonitoringInteraction } from "./analytics";
import { Button } from "../Button";
import { ListTable } from "../ListTable";
import { Modal } from "../Modal";
import { OverflowBadges } from "../Settings/OverflowBadges";
import { PillBadge, SearchField } from "../Settings/shared";
import { RadarChart } from "../RadarChart";
import { Text } from "../Text";
import { WorkflowBadge } from "../WorkflowBadge";
import {
  DETAIL_MODAL_STYLE,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderRight,
  ModalTitle,
} from "./MonitoringDetailCard";
import { ListSection, NameBody, NameCell } from "./monitoringList";
import { buildRouteAuthSegments, routeLabel } from "./routeAuthSegments";
import {
  useMonitoringSpaces,
  useMonitoringWorkflowRoutes,
  useMonitoringWorkflows,
} from "./useMonitoringWorkflows";
import { Panel, PanelHeader, PanelSkeleton, TotalRow } from "./workflowsLayout";

function parseRouteSort(value: string | undefined): WorkflowRouteSort {
  return value === "routes" ? "routes" : "name";
}

export function RouteAuthPanel() {
  const makeUrl = useMakeUrl();
  const recordInteraction = useMonitoringInteraction();
  const {
    sort: sortParam,
    panel,
    auth,
    search: searchParam,
  } = useCurrentUrl(routes.monitoring.workflows);
  const routeSort = parseRouteSort(sortParam);
  const search = searchParam ?? "";
  const open = panel === "routes";
  const authFilter = (ROUTE_AUTH_MODES as readonly string[]).includes(
    auth ?? ""
  )
    ? (auth as RouteAuthMode)
    : null;
  const setSearch = (value: string) =>
    navigate(routes.monitoring.workflows, { search: value }, { replace: true });
  const close = () =>
    navigate(
      routes.monitoring.workflows,
      { panel: "", auth: "", search: "" },
      { replace: true }
    );
  const { report, loading } = useMonitoringWorkflows();
  const { workflows: routeWorkflows, loading: routesLoading } =
    useMonitoringWorkflowRoutes(open);
  const { spaceById, loading: spacesLoading } = useMonitoringSpaces();
  const [tenant, tenantResult] = useQuery(queries.currentTenant());
  const displayName = useSpaceDisplayName();
  const routeSummary = report.routeSummary;
  const displayRoute = (route: MonitorRouteRow) =>
    routeLabel(route, config.routeAuthForceDisabled, tenant);

  const workflowById = useMemo(
    () => new Map(routeWorkflows.map((workflow) => [workflow.id, workflow])),
    [routeWorkflows]
  );

  const workflowSummaries = useMemo(
    () => groupRoutesByWorkflow(computeMonitorRoutes(routeWorkflows)),
    [routeWorkflows]
  );

  const authSegments = buildRouteAuthSegments(
    routeSummary,
    config.routeAuthForceDisabled,
    tenant
  );

  const filteredSummaries = useMemo(
    () => filterWorkflowSummaries(workflowSummaries, authFilter),
    [workflowSummaries, authFilter]
  );

  const sortedSummaries = useMemo(
    () => sortWorkflowRouteSummaries(filteredSummaries, routeSort, authFilter),
    [filteredSummaries, routeSort, authFilter]
  );

  const displayedSummaries = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return sortedSummaries;
    return sortedSummaries.filter((summary) =>
      summary.workflowName.toLowerCase().includes(q)
    );
  }, [sortedSummaries, search]);

  const setRouteSort = (next: WorkflowRouteSort) => {
    recordInteraction("workflows.route_sort_change", { value: next });
    navigate(routes.monitoring.workflows, { sort: next }, { replace: true });
  };

  if (loading || spacesLoading || tenantResult.type === "unknown") {
    return <PanelSkeleton />;
  }

  if (tenantResult.type === "error" || !tenant) {
    return (
      <Panel>
        <PanelHeader>
          <Text variant="text-small-md">Workflow routes</Text>
        </PanelHeader>
        <Text variant="text-regular" color="var(--text-secondary)">
          We couldn’t load security settings to check route access. Reload to
          try again.
        </Text>
      </Panel>
    );
  }

  return (
    <Panel>
      <PanelHeader>
        <Text variant="text-small-md">Workflow routes</Text>
        <TotalRow>
          <Text variant="display-md">{routeSummary.workflowCount}</Text>
        </TotalRow>
      </PanelHeader>

      {routeSummary.workflowCount > 0 ? (
        <RadarChart
          segments={authSegments}
          onSegmentClick={(s) => {
            recordInteraction("workflows.panel_drilldown_open", {
              value: "routes",
            });
            navigate(routes.monitoring.workflows, {
              panel: "routes",
              auth: s.key,
              search: "",
            });
          }}
        />
      ) : (
        <RadarChart segments={authSegments} placeholder />
      )}

      <AskMonitoringChat
        surface="routes"
        context={{
          scope: "both",
          filters: { spaceId: null, authMode: null, view: "summary" },
        }}
        snapshot={JSON.stringify(routeSummary)}
      />

      <Modal open={open} onClose={close} style={DETAIL_MODAL_STYLE}>
        <ModalHeader>
          <ModalTitle icon="link-2" title="Workflow routes" />
          <ModalHeaderRight>
            <AskMonitoringChat
              surface="routes"
              alwaysVisible
              inline
              includeSearch
              disabled={routesLoading}
              context={{
                scope: "both",
                filters: {
                  spaceId: null,
                  authMode: authFilter,
                  view: "workflows",
                },
              }}
              snapshot={JSON.stringify({
                summary: routeSummary,
                filteredWorkflowCount: displayedSummaries.length,
                workflowIds: displayedSummaries
                  .slice(0, 50)
                  .map((row) => row.workflowId),
                includesAllMatchingWorkflows: displayedSummaries.length <= 50,
              })}
            />
            <Button size="l" variant="secondary" icon="x" onClick={close} />
          </ModalHeaderRight>
        </ModalHeader>

        <ModalBody style={{ gap: 0 }}>
          <ListSection
            title={
              authFilter ? `${routeAuthLabel(authFilter)} routes` : "Routes"
            }
            description="The workflows configured with these routes, including routes disabled by security settings."
            action={
              <SearchField
                placeholder="Search workflows…"
                value={search}
                onChange={setSearch}
                width={280}
                autoFocus
              />
            }
          >
            <ListTable
              headers={{
                name: "Workflow",
                count: "Routes",
                matching: "Matching routes",
              }}
              columns={{ name: "flex", count: "content", matching: "flex" }}
              sortableColumns={["name", "count"]}
              sortColumn={routeSort === "routes" ? "count" : "name"}
              sortDirection={routeSort === "name" ? "asc" : "desc"}
              onSort={(col) =>
                setRouteSort(col === "count" ? "routes" : "name")
              }
              emptyState={
                routesLoading ? null : (
                  <Text variant="text-regular" color="var(--text-secondary)">
                    {search
                      ? "No workflows match your search."
                      : "No workflows match this auth type and filter."}
                  </Text>
                )
              }
              items={displayedSummaries.map((summary) => {
                const workflow = workflowById.get(summary.workflowId);
                const space = spaceById.get(summary.spaceId);
                const matching = matchingRoutes(summary, authFilter);
                return {
                  key: summary.workflowId,
                  link: makeUrl(routes.space.workflow, {
                    spaceId: summary.spaceId,
                    workflowId: summary.workflowId,
                  }),
                  onNavigate: () =>
                    recordInteraction("workflows.workflow_link_click", {
                      targetType: "workflow",
                      targetId: summary.workflowId,
                    }),
                  name: (hover: boolean) => (
                    <NameCell>
                      {workflow ? (
                        <WorkflowBadge
                          workflow={{
                            id: workflow.id,
                            badgeSeed: workflow.badgeSeed,
                            pausedAt: workflow.pausedAt,
                            branches: [{ name: "main" }],
                          }}
                          hover={hover}
                        />
                      ) : null}
                      <NameBody>
                        <Text variant="text-regular-md" ellipsis>
                          {summary.workflowName}
                        </Text>
                        {space ? (
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                            ellipsis
                          >
                            {displayName(space)}
                          </Text>
                        ) : null}
                      </NameBody>
                    </NameCell>
                  ),
                  count: <Text variant="text-small">{matching.length}</Text>,
                  matching: (
                    <OverflowBadges
                      items={matching}
                      keyFn={(route) => route.route}
                      renderBadge={(route) => (
                        <PillBadge>{displayRoute(route)}</PillBadge>
                      )}
                      overflowTooltip={(hidden) =>
                        hidden.map(displayRoute).join(", ")
                      }
                    />
                  ),
                };
              })}
            />
          </ListSection>
        </ModalBody>

        <ModalFooter>
          <Text variant="text-small" color="var(--text-secondary)">
            {sortedSummaries.length}{" "}
            {sortedSummaries.length === 1 ? "workflow" : "workflows"}
          </Text>
          <Button size="l" variant="primary" onClick={close}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </Panel>
  );
}
